import React, { createContext, useCallback, useContext, useMemo } from 'react';

type QueryKey = readonly unknown[];

type CacheAvailabilityContextType = {
  isQueryKeyAvailable: (queryKey: QueryKey) => boolean;
  availableQueryKeys: Set<string>;
};

export const CacheAvailability = createContext<CacheAvailabilityContextType>({
  isQueryKeyAvailable: () => false,
  availableQueryKeys: new Set(),
});

type Props = {
  children: React.ReactNode;
  queryKeys: Set<QueryKey>;
};

// TODO: Can improve this to support patterns/wildcard matching if we need it
export function CacheAvailabilityProvider({ children, queryKeys }: Props) {
  const parentCache = useContext(CacheAvailability);

  const availableQueryKeys = useMemo(() => {
    const normalizedKeys = new Set<string>(parentCache.availableQueryKeys);

    // Add current level keys (normalize them from arrays to strings)
    queryKeys.forEach((queryKey) => {
      normalizedKeys.add(JSON.stringify(queryKey));
    });

    return normalizedKeys;
  }, [parentCache, queryKeys]);

  const isQueryKeyAvailable = useCallback(
    (queryKey: QueryKey) => availableQueryKeys.has(JSON.stringify(queryKey)),
    [availableQueryKeys],
  );

  return (
    <CacheAvailability.Provider
      value={{
        isQueryKeyAvailable,
        availableQueryKeys,
      }}
    >
      {children}
    </CacheAvailability.Provider>
  );
}

export function useCacheAvailability(): CacheAvailabilityContextType {
  return useContext(CacheAvailability);
}
